/*---------------------------------------------------------------
 ?? Project: Ring2All Platform
 ????? Author: Rodrigo Cuadra
 ?? Company: Ring2All LLC
 ?? File: pages\dashboards\widgets\NetworkInfoWidget.tsx
 ??? Created: 2026-05-02
 ?? Description:
    Implementation of the PBX Tools module.
----------------------------------------------------------------*/


import { useState, useEffect, useContext } from 'react';
import { Network, Wifi, Shield, Server } from 'lucide-react';
import { WidgetContext } from '../components/WidgetWrapper';

// Default widget preferences for card styling (matching SystemStatsWidget)
const DEFAULT_CARD_COLOR = '#1e293b';
const DEFAULT_BORDER_COLOR = '#334155';

interface NetworkInterface {
    interface: string;
    address: string;
    netmask: string;
    mac: string;
}

interface NetworkInfoWidgetProps {
    config?: Record<string, any>;
    isEditMode?: boolean;
}

export function NetworkInfoWidget({ isEditMode }: NetworkInfoWidgetProps) {
    const [interfaces, setInterfaces] = useState<NetworkInterface[]>([]);
    const [hostname, setHostname] = useState<string>('');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // Get widget context for dynamic styling
    const widgetPrefs = useContext(WidgetContext);
    const cardColor = widgetPrefs?.cardColor || DEFAULT_CARD_COLOR;
    const borderColor = widgetPrefs?.borderColor || DEFAULT_BORDER_COLOR;

    useEffect(() => {
        const fetchInfo = async () => {
            try {
                const protocol = window.location.protocol;
                const host = window.location.host;
                const response = await fetch(`${protocol}//${host}/monitoring-api/api/system/stats`);

                if (response.ok) {
                    const data = await response.json();
                    if (data.success && data.data) {
                        if (data.data.network) {
                            setInterfaces(data.data.network);
                        } else {
                            setInterfaces([]);
                        }

                        if (data.data.hostname) {
                            setHostname(data.data.hostname);
                        }
                    }
                } else {
                    setError('Failed to fetch stats');
                }
            } catch (err) {
                console.error('Error fetching network info:', err);
                setError('Connection failed');
            } finally {
                setLoading(false);
            }
        };

        fetchInfo();
        const interval = setInterval(fetchInfo, 30000);
        return () => clearInterval(interval);
    }, []);

    if (loading) {
        return (
            <div className="h-full flex items-center justify-center">
                <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
            </div>
        );
    }

    if (error) {
        return (
            <div className="h-full flex flex-col items-center justify-center text-destructive p-4 text-center">
                <Network className="w-8 h-8 mb-2 opacity-50" />
                <span className="text-sm font-medium">{error}</span>
            </div>
        );
    }

    return (
        <div className="h-full pr-1 overflow-hidden flex flex-col">
            <div className="flex-1 flex flex-col pt-1 pb-1 overflow-y-auto">
                {/* Hostname */}
                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center border-b border-border/40 pb-1 mb-1">
                    <span className="text-sm text-muted-foreground font-medium">Hostname</span>
                    <span className="text-sm font-medium text-foreground text-right truncate max-w-[12rem]">{hostname || 'Unknown'}</span>
                </div>

                {/* Interfaces */}
                {interfaces.map((iface, idx) => (
                    <div key={iface.interface} className="flex flex-col mb-2 last:mb-0">
                        {/* Interface Header */}
                        <div className="flex items-center gap-2 mb-1 mt-1">
                            <Network className="w-4 h-4 text-primary" />
                            <span className="text-sm font-bold text-foreground">{iface.interface}</span>
                        </div>

                        {/* Details */}
                        <div className="pl-2 border-l-2 border-border/30 ml-1 space-y-1">
                            <div className="flex justify-between items-center">
                                <span className="text-xs text-muted-foreground font-medium">IP Address</span>
                                <span className="text-xs font-mono text-foreground">{iface.address}</span>
                            </div>
                            <div className="flex justify-between items-center">
                                <span className="text-xs text-muted-foreground font-medium">Netmask</span>
                                <span className="text-xs font-mono text-foreground">{iface.netmask}</span>
                            </div>
                            <div className="flex justify-between items-center">
                                <span className="text-xs text-muted-foreground font-medium">MAC</span>
                                <span className="text-xs font-mono text-foreground">{iface.mac}</span>
                            </div>
                        </div>
                    </div>
                ))}

                {interfaces.length === 0 && (
                    <div className="flex items-center justify-center h-20 text-muted-foreground">
                        <span className="text-sm">No interfaces found</span>
                    </div>
                )}
            </div>
        </div>
    );
}
